import type { GraphEntityType, GraphFrameCommitResponse, GraphNode, GraphVisualizationFrame } from "@salesko/contracts";
import { canonicalEntityIdForNode, resolveSelfNodeId } from "@salesko/contracts";
import { useEffect, useMemo, useState } from "react";
import { setServerSelfNode } from "../../lib/api";
import type { GraphViewMode } from "../../components/GraphCanvas";
import { entityMeta } from "../entityMeta";
import { graphSearchScore } from "../lib/search";
import { acceptReviewCandidate, discardReviewCandidate } from "../lib/review";
import { deriveTimeline } from "../lib/timeline";
import type { DetailTab } from "../types";

const allEntityTypes: GraphEntityType[] = ["person", "organisation", "event", "opportunity", "note"];
const minGraphZoom = 0.65;
const maxGraphZoom = 1.8;
const graphZoomStep = 0.16;
// Interim client-side ego-center designation. Single-user product, so a per-browser
// key is acceptable until authoritative server persistence lands (see resolveSelfNodeId).
const selfStorageKey = "salesko:self-canonical-entity-id";

type WorkspaceFrameResult = {
  frame: GraphVisualizationFrame;
  commit: GraphFrameCommitResponse;
};

export function useWorkspaceFrameState(defaultFrame: GraphVisualizationFrame) {
  const [detailTab, setDetailTab] = useState<DetailTab>("companies");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(() => new Set());
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewMode, setViewMode] = useState<GraphViewMode>("flat");
  const [timelineFocus, setTimelineFocus] = useState(0);
  const [filters, setFilters] = useState<Record<GraphEntityType, boolean>>({
    event: true,
    note: true,
    opportunity: true,
    organisation: true,
    person: true
  });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<WorkspaceFrameResult | null>(null);
  const [selfCanonicalEntityId, setSelfCanonicalEntityId] = useState<string | null>(() =>
    typeof window === "undefined" ? null : window.localStorage.getItem(selfStorageKey)
  );

  const baseFrame = lastResult?.frame ?? defaultFrame;
  // Resolve the designated self canonical id to a node in this frame and stamp
  // ui.selfNodeId. Fail-closed: if nothing is designated or no node matches, the base
  // frame passes through untouched (a fixture may already carry its own selfNodeId).
  const frame = useMemo(() => {
    const resolved = resolveSelfNodeId(baseFrame, selfCanonicalEntityId);
    if (!resolved || baseFrame.ui.selfNodeId === resolved) {
      return baseFrame;
    }
    return { ...baseFrame, ui: { ...baseFrame.ui, selfNodeId: resolved } };
  }, [baseFrame, selfCanonicalEntityId]);
  const timeline = useMemo(() => deriveTimeline(frame), [frame]);
  const selectedNode = useMemo(() => {
    return frame.graph.nodes.find((node) => node.id === selectedNodeId) ?? null;
  }, [frame, selectedNodeId]);
  const selectedEdge = useMemo(() => {
    return frame.graph.edges.find((edge) => edge.id === selectedEdgeId) ?? null;
  }, [frame, selectedEdgeId]);

  useEffect(() => {
    if (selectedNodeId && !frame.graph.nodes.some((node) => node.id === selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [frame, selectedNodeId]);

  useEffect(() => {
    if (!selectedEdgeId || frame.graph.edges.some((edge) => edge.id === selectedEdgeId)) {
      return;
    }
    setSelectedEdgeId(null);
  }, [frame, selectedEdgeId]);

  // Reset the timeline scrub to the latest moment per frame, and fall back to the graph
  // only when the current view is timeline but the frame can't sustain a timeline.
  useEffect(() => {
    setViewMode((current) => (!timeline.enabled && current === "timeline" ? "flat" : current));
    setTimelineFocus(timeline.enabled ? Math.max(0, timeline.moments.length - 1) : 0);
  }, [timeline]);

  const linkedNodes = useMemo(() => linkedEntitiesFor(frame, selectedNode), [frame, selectedNode]);
  const visibleLinkedNodes = useMemo(
    () => linkedNodes.filter((node) => entityMeta[node.type].tab === detailTab),
    [detailTab, linkedNodes]
  );
  const enabledEntityTypes = useMemo(
    () => allEntityTypes.filter((entityType) => filters[entityType] !== false),
    [filters]
  );
  const enabledTypeSet = useMemo(() => new Set(enabledEntityTypes), [enabledEntityTypes]);
  const searchTerm = searchQuery.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (searchTerm.length === 0) {
      return [];
    }

    return frame.graph.nodes
      .map((node) => ({
        node,
        score: graphSearchScore(node, searchTerm)
      }))
      .filter(({ node, score }) => enabledTypeSet.has(node.type) && score > 0)
      .sort((left, right) => right.score - left.score || left.node.label.localeCompare(right.node.label))
      .map(({ node }) => node);
  }, [enabledTypeSet, frame, searchTerm]);
  const searchResultIds = useMemo(() => searchResults.map((node) => node.id), [searchResults]);
  const expandedNeighborIds = useMemo(() => neighborIdsFor(frame, expandedNodeIds), [expandedNodeIds, frame]);
  const spotlightNodeIds = useMemo(() => {
    const ids = new Set<string>();

    if (selectedNode) {
      ids.add(selectedNode.id);
      for (const node of linkedNodes) {
        ids.add(node.id);
      }
    }

    for (const id of expandedNodeIds) {
      ids.add(id);
    }
    for (const id of expandedNeighborIds) {
      ids.add(id);
    }
    for (const id of searchResultIds) {
      ids.add(id);
    }

    return Array.from(ids);
  }, [expandedNeighborIds, expandedNodeIds, linkedNodes, searchResultIds, selectedNode]);
  const detailTabs = useMemo(
    () =>
      [
        { count: linkedNodes.filter((node) => node.type === "person").length, id: "people" as const },
        { count: linkedNodes.filter((node) => node.type === "event").length, id: "events" as const },
        {
          count: linkedNodes.filter((node) => node.type === "organisation").length,
          id: "companies" as const
        },
        {
          count: linkedNodes.filter((node) => node.type === "opportunity").length,
          id: "opportunities" as const
        }
      ],
    [linkedNodes]
  );

  // Picking a new entity must not land on an empty tab while its only links sit in
  // an unselected one. Keyed on selectedNodeId alone: once the user is on a node,
  // an explicit choice of an empty tab survives every later linkedNodes change.
  useEffect(() => {
    if (!selectedNodeId) {
      return;
    }

    setDetailTab((current) => {
      if ((detailTabs.find((tab) => tab.id === current)?.count ?? 0) > 0) {
        return current;
      }
      return detailTabs.find((tab) => tab.count > 0)?.id ?? current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  const updateWorkspaceFrame = (nextFrame: GraphVisualizationFrame, commit: GraphFrameCommitResponse) => {
    setLastResult({ frame: nextFrame, commit });
    // Node ids are frame-scoped. A different saved frame must not inherit a
    // coincidentally equal id; the caller can explicitly select a restore target.
    setSelectedNodeId((current) =>
      lastResult?.commit.id === commit.id && nextFrame.graph.nodes.some((node) => node.id === current) ? current : null
    );
    setSelectedEdgeId(null);
    setExpandedNodeIds(new Set());
    setSearchQuery("");
    setZoomLevel(1);
    setFilters((current) => {
      const next = { ...current };
      for (const filter of nextFrame.ui.filters) {
        next[filter.entityType] = filter.enabled;
      }
      return next;
    });
  };

  const updateLocalReviewedFrame = (nextFrame: GraphVisualizationFrame): WorkspaceFrameResult | null => {
    if (!lastResult) {
      return null;
    }

    const nextResult = {
      frame: nextFrame,
      commit: {
        ...lastResult.commit,
        status: nextFrame.needsReview ? "needs_review" : "accepted",
        nextAction: nextFrame.needsReview ? "review" : "persist-graph"
      }
    } satisfies WorkspaceFrameResult;

    setLastResult(nextResult);
    setSelectedNodeId((current) => nextFrame.graph.nodes.some((node) => node.id === current) ? current : null);
    setSelectedEdgeId(null);
    setExpandedNodeIds(new Set());

    return nextResult;
  };

  const acceptSelectedReview = (nodeId: string) => {
    return updateLocalReviewedFrame(acceptReviewCandidate(frame, nodeId));
  };

  const discardSelectedReview = (nodeId: string) => {
    return updateLocalReviewedFrame(discardReviewCandidate(frame, nodeId));
  };

  const handleSelectNode = (nodeId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
    setExpandedNodeIds((current) => {
      const next = new Set(current);
      next.add(nodeId);
      return next;
    });
  };

  const handleSelectEdge = (edgeId: string) => {
    const edge = frame.graph.edges.find((candidate) => candidate.id === edgeId);
    if (!edge) {
      return;
    }

    setSelectedEdgeId(edge.id);
    setSelectedNodeId((current) => (current === edge.source || current === edge.target ? current : edge.source));
    setExpandedNodeIds((current) => {
      const next = new Set(current);
      next.add(edge.source);
      next.add(edge.target);
      return next;
    });
  };

  const handleRecenter = () => {
    const nextNodeId = frame.camera.focusNodeId ?? frame.ui.detailPanel.selectedNodeId ?? frame.graph.nodes[0]?.id ?? null;
    setSelectedNodeId(nextNodeId);
    setSelectedEdgeId(null);
    setExpandedNodeIds(new Set());
    setSearchQuery("");
    setZoomLevel(1);
    if (timeline.enabled) {
      setTimelineFocus(Math.max(0, timeline.moments.length - 1));
    }
  };

  const handleViewModeChange = (mode: GraphViewMode) => {
    if (mode === "timeline" && !timeline.enabled) {
      return;
    }
    setViewMode(mode);
  };

  const handleZoomIn = () => {
    setZoomLevel((current) => clampGraphZoom(current + graphZoomStep));
  };

  const handleZoomOut = () => {
    setZoomLevel((current) => clampGraphZoom(current - graphZoomStep));
  };

  const setSelfNode = (targetNode: GraphNode) => {
    const canonicalId = canonicalEntityIdForNode(targetNode.type, targetNode.label);
    setSelfCanonicalEntityId(canonicalId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(selfStorageKey, canonicalId);
    }
    // Best-effort authoritative persistence; localStorage is the optimistic local source.
    void setServerSelfNode(canonicalId).catch(() => undefined);
  };

  const clearSelfNode = () => {
    setSelfCanonicalEntityId(null);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(selfStorageKey);
    }
    void setServerSelfNode(null).catch(() => undefined);
  };

  return {
    acceptSelectedReview,
    clearSelfNode,
    clearSelection: () => { setSelectedNodeId(null); setSelectedEdgeId(null); },
    detailTab,
    detailTabs,
    discardSelectedReview,
    enabledEntityTypes,
    filters,
    frame,
    handleRecenter,
    handleSelectEdge,
    handleSelectNode,
    handleViewModeChange,
    handleZoomIn,
    handleZoomOut,
    lastResult,
    searchQuery,
    searchResultIds,
    searchResults,
    selectedEdge,
    selectedEdgeId,
    selectedNode,
    selectedNodeId,
    selfNodeId: frame.ui.selfNodeId ?? null,
    setClampedZoomLevel: (value: number) => setZoomLevel(clampGraphZoom(value)),
    setSelfNode,
    setDetailTab,
    setFilters,
    setSearchQuery,
    setTimelineFocus,
    spotlightNodeIds,
    timeline,
    timelineFocus,
    updateWorkspaceFrame,
    viewMode,
    visibleLinkedNodes,
    zoomLevel
  };
}

function linkedEntitiesFor(frame: GraphVisualizationFrame, selectedNode: GraphNode | null) {
  if (!selectedNode) {
    return [];
  }

  const linkedIds = new Set<string>();
  if (selectedNode.id === frame.ui.detailPanel.selectedNodeId) {
    for (const id of frame.ui.detailPanel.linkedNodeIds) {
      linkedIds.add(id);
    }
  }

  for (const edge of frame.graph.edges) {
    if (edge.source === selectedNode.id) {
      linkedIds.add(edge.target);
    }
    if (edge.target === selectedNode.id) {
      linkedIds.add(edge.source);
    }
  }

  return frame.graph.nodes.filter((node) => linkedIds.has(node.id));
}

function neighborIdsFor(frame: GraphVisualizationFrame, nodeIds: Set<string>) {
  const ids = new Set<string>();
  if (nodeIds.size === 0) {
    return ids;
  }

  for (const edge of frame.graph.edges) {
    if (nodeIds.has(edge.source)) {
      ids.add(edge.target);
    }
    if (nodeIds.has(edge.target)) {
      ids.add(edge.source);
    }
  }

  return ids;
}

function clampGraphZoom(value: number) {
  return Math.max(minGraphZoom, Math.min(maxGraphZoom, value));
}
